import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { useAuth } from "@/auth/AuthContext";
import { createApiClient } from "@/api/client";
import { queryKeys } from "@/api/queryClient";
import type {
  InstantiatorGrantView,
  ProcessInstanceSummaryView,
  ProcessInstanceView,
  TemplateSpecView,
} from "@/types";

// #300: Instanz-Ebene. Sichtbarkeit entscheidet der Master (Admin alles, Template-Owner
// die Instanzen seines Templates, sonst nur eigene) — die UI filtert nichts nach.

export function useProcessInstances(templateId?: string): UseQueryResult<ProcessInstanceSummaryView[]> {
  const { session, logout } = useAuth();
  return useQuery({
    queryKey: [...queryKeys.list("process-instances"), templateId ?? "all"],
    queryFn: () => {
      const client = createApiClient({ getToken: () => session?.accessToken ?? null, onUnauthorized: logout });
      const q = templateId ? `?templateId=${encodeURIComponent(templateId)}` : "";
      return client.get<ProcessInstanceSummaryView[]>(`/process-instances${q}`);
    },
    enabled: !!session,
  });
}

export function useProcessInstance(id: string | undefined): UseQueryResult<ProcessInstanceView> {
  const { session, logout } = useAuth();
  return useQuery({
    queryKey: id ? queryKeys.detail("process-instances", id) : ["resources", "process-instances", "_invalid"],
    queryFn: () => {
      const client = createApiClient({ getToken: () => session?.accessToken ?? null, onUnauthorized: logout });
      return client.get<ProcessInstanceView>(`/process-instances/${id}`);
    },
    enabled: !!session && !!id,
  });
}

// Konfigurations-Sicht der aktiven Template-Version — für die Instanz-Formulare.
export function useTemplateSpec(id: string | undefined): UseQueryResult<TemplateSpecView> {
  const { session, logout } = useAuth();
  return useQuery({
    queryKey: id
      ? queryKeys.subResource("process-instances", id, "template-spec")
      : ["resources", "process-instances", "_invalid", "template-spec"],
    queryFn: () => {
      const client = createApiClient({ getToken: () => session?.accessToken ?? null, onUnauthorized: logout });
      return client.get<TemplateSpecView>(`/process-instances/${id}/template-spec`);
    },
    enabled: !!session && !!id,
  });
}

export function useInstantiators(processId: string | undefined): UseQueryResult<InstantiatorGrantView[]> {
  const { session, logout } = useAuth();
  return useQuery({
    queryKey: processId
      ? queryKeys.subResource("processes", processId, "instantiators")
      : ["resources", "processes", "_invalid", "instantiators"],
    queryFn: () => {
      const client = createApiClient({ getToken: () => session?.accessToken ?? null, onUnauthorized: logout });
      return client.get<InstantiatorGrantView[]>(`/processes/${processId}/instantiators`);
    },
    enabled: !!session && !!processId,
  });
}
